import { FRICTION_KINDS, SESSION_OUTCOMES, type SessionQuery, type SessionSummary } from "@ti/core";
import { useQueries, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Loader2, MessagesSquare } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { z } from "zod";
import { OutcomeMixBar, SuccessRate } from "@/components/domain/analytics";
import { PageHeader } from "@/components/domain/misc";
import { ErrorPanel, Page } from "@/components/layout/page";
import { Button } from "@/components/ui/button";
import { Card, CardHeader } from "@/components/ui/card";
import { DataTable } from "@/components/ui/data-table";
import { Skeleton } from "@/components/ui/misc";
import { sessionColumns } from "@/features/sessions/session-columns";
import { RANGES, SessionFilterBar, type SessionFilterValues } from "@/features/sessions/session-filters";
import { q } from "@/lib/api";
import { fmtInt, fmtPct } from "@/lib/format";

const PAGE_SIZE = 50;

const searchSchema = z.object({
  q: z.string().optional().catch(undefined),
  agentId: z.string().min(1).optional().catch(undefined),
  useCaseId: z.string().min(1).optional().catch(undefined),
  outcome: z.enum(SESSION_OUTCOMES).optional().catch(undefined),
  friction: z.enum(FRICTION_KINDS).optional().catch(undefined),
  signal: z.string().min(1).optional().catch(undefined),
  segment: z.string().min(1).optional().catch(undefined),
  funnelId: z.string().min(1).optional().catch(undefined),
  droppedAfterStep: z.coerce.number().int().min(0).optional().catch(undefined),
  painPointId: z.string().min(1).optional().catch(undefined),
  range: z.enum(["24h", "7d"]).optional().catch(undefined),
});

export const Route = createFileRoute("/_app/sessions/")({
  validateSearch: searchSchema,
  component: SessionsPage,
});

function SessionsPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const setSearch = (patch: Partial<SessionFilterValues>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  const clear = () => void navigate({ search: {}, replace: true });
  const onSegment = useCallback((segment: string) => void navigate({ search: (prev) => ({ ...prev, segment }), replace: true }), [navigate]);

  // Relative ranges resolve to an absolute `from`, rounded to the minute so the query key is stable.
  const minute = Math.floor(Date.now() / 60_000) * 60_000;
  const from = search.range ? new Date(minute - RANGES[search.range]).toISOString() : undefined;
  const filters: Partial<SessionQuery> = useMemo(
    () => ({
      q: search.q,
      agentId: search.agentId,
      useCaseId: search.useCaseId,
      outcome: search.outcome,
      friction: search.friction,
      signal: search.signal,
      segment: search.segment,
      funnelId: search.funnelId,
      droppedAfterStep: search.funnelId ? search.droppedAfterStep : undefined,
      painPointId: search.painPointId,
      from,
    }),
    [search.q, search.agentId, search.useCaseId, search.outcome, search.friction, search.signal, search.segment, search.funnelId, search.droppedAfterStep, search.painPointId, from],
  );
  const hasFilters = Object.values(filters).some((v) => v !== undefined);

  return (
    <Page wide>
      <PageHeader
        title="Sessions"
        description="Every conversation and task run, enriched once into intent, use case, outcome, friction and funnel steps. Open one for the annotated transcript."
      />
      <SessionFilterBar values={search} onChange={setSearch} onClear={clear} />
      <div className="flex flex-col gap-3">
        <OutcomeMixCard filters={filters} />
        <SessionResults filters={filters} hasFilters={hasFilters} onClear={clear} onSegment={onSegment} />
      </div>
    </Page>
  );
}

/** Outcome mix of every session matching the filters, aggregated server-side over the same filters as the list. */
function OutcomeMixCard({ filters }: { filters: Partial<SessionQuery> }) {
  const stats = useQuery(q.sessionStats(filters));
  const s = stats.data;
  const pending = s ? Math.round(s.total * (1 - s.enrichedShare)) : 0;
  const enriched = s ? s.total - pending : 0;
  const stale = stats.isPlaceholderData;

  if (stats.error) return null;
  return (
    <Card className={stale ? "opacity-60 transition-opacity" : "transition-opacity"}>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 px-4 py-3">
        <div className="flex flex-col">
          <span className="text-[11px] font-medium uppercase tracking-wide text-fg-3">Outcome mix</span>
          {!s ? (
            <Skeleton className="mt-1 h-4 w-40" />
          ) : (
            <span className="text-[13px] text-fg-2">
              <span className="font-medium tabular text-fg">{fmtInt(s.total)}</span> sessions · success{" "}
              {enriched ? <SuccessRate value={s.successRate} /> : <span className="text-fg-3">—</span>}
              {enriched ? (
                <>
                  {" "}
                  · frustration <span className="tabular text-fg">{fmtPct(s.frustrationRate)}</span> · <span className="tabular text-fg">{s.avgTurns.toFixed(1)}</span> turns avg
                </>
              ) : null}
            </span>
          )}
        </div>
        <div className="min-w-[320px] flex-1">{!s ? <Skeleton className="h-3 w-full" /> : <OutcomeMixBar mix={s.outcomes} height={10} showLegend />}</div>
        {pending > 0 ? <span className="text-[11px] text-fg-3 tabular">{fmtInt(pending)} not enriched yet</span> : null}
      </div>
    </Card>
  );
}

function SessionResults({ filters, hasFilters, onClear, onSegment }: { filters: Partial<SessionQuery>; hasFilters: boolean; onClear: () => void; onSegment: (segment: string) => void }) {
  const navigate = Route.useNavigate();
  const filterKey = JSON.stringify(filters);
  // Cursors for "Load more"; reset whenever the filters change.
  const [paging, setPaging] = useState<{ key: string; cursors: Array<string | undefined> }>({ key: filterKey, cursors: [undefined] });
  const cursors = paging.key === filterKey ? paging.cursors : [undefined];
  const columns = useMemo(() => sessionColumns({ onSegment }), [onSegment]);

  const pages = useQueries({ queries: cursors.map((cursor) => q.sessions({ ...filters, limit: PAGE_SIZE, cursor })) });
  const first = pages[0]!;
  const last = pages[pages.length - 1]!;
  const seen = new Set<string>();
  const items: SessionSummary[] = [];
  for (const p of pages)
    for (const s of p.data?.items ?? []) {
      if (seen.has(s.id)) continue;
      seen.add(s.id);
      items.push(s);
    }

  const total = first.data?.total;
  const nextCursor = last.data && !last.isPlaceholderData ? last.data.nextCursor : null;
  const loadingMore = pages.length > 1 && last.isFetching;
  const refreshing = first.isPlaceholderData && first.isFetching;

  if (first.error) {
    return (
      <Card>
        <ErrorPanel error={first.error} reset={() => void first.refetch()} className="m-4" />
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        title="Results"
        description={total === undefined ? "Loading…" : `${fmtInt(total)} ${total === 1 ? "session" : "sessions"}${hasFilters ? " match" : ""} · newest first`}
        actions={refreshing ? <Loader2 className="size-3.5 animate-spin text-fg-3" aria-label="Updating results" /> : null}
      />
      <DataTable
        data={first.isPending ? undefined : items}
        loading={first.isPending}
        columns={columns}
        dense
        stickyHeader={false}
        getRowId={(s) => s.id}
        onRowClick={(s) => void navigate({ to: "/sessions/$sessionId", params: { sessionId: s.id } })}
        className={refreshing ? "opacity-60 transition-opacity" : "transition-opacity"}
        empty={{
          icon: <MessagesSquare />,
          title: hasFilters ? "No sessions match these filters" : "No sessions yet",
          description: hasFilters ? (
            <>
              Try widening the filters or{" "}
              <button type="button" className="cursor-pointer text-accent hover:underline" onClick={onClear}>
                clear all filters
              </button>
              .
            </>
          ) : (
            "Sessions appear as soon as traces arrive with a session id, or are grouped by end user and idle timeout."
          ),
        }}
      />
      {items.length ? (
        <div className="flex items-center justify-between gap-3 border-t px-4 py-2.5 text-xs text-fg-3">
          <span className="tabular">
            Showing {fmtInt(items.length)} of {fmtInt(total ?? items.length)}
          </span>
          {nextCursor ? (
            <Button variant="outline" size="sm" disabled={loadingMore} onClick={() => setPaging({ key: filterKey, cursors: [...cursors, nextCursor] })}>
              {loadingMore ? <Loader2 className="animate-spin" /> : null}
              Load {fmtInt(Math.min(PAGE_SIZE, (total ?? 0) - items.length))} more
            </Button>
          ) : loadingMore ? (
            <Loader2 className="size-3.5 animate-spin" aria-label="Loading more" />
          ) : (
            <span>End of results</span>
          )}
        </div>
      ) : null}
    </Card>
  );
}
